import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FiArrowRight, FiLinkedin, FiYoutube, FiGithub, FiMail } from 'react-icons/fi';
import LogoCloud from '../components/LogoCloud';
import GitHubChart from '../components/GitHubChart';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.05 } }
};

const itemVariants = {
  hidden: { opacity: 0, y: 8 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.2 } }
};

function Home() {
  const [heroRef, heroInView] = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    <>
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-white dark:bg-gray-900">

        <motion.div
          ref={heroRef}
          variants={containerVariants}
          initial="hidden"
          animate={heroInView ? "visible" : "hidden"}
          className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20"
        >
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Text Content */}
            <motion.div variants={itemVariants} className="text-center lg:text-left">
              <motion.h1
                variants={itemVariants}
                className="mb-6 leading-tight"
              >
                <span className="block text-gray-900 dark:text-white text-5xl md:text-6xl lg:text-7xl font-extrabold mb-4">
                  Hi, I'm Daniel.
                </span>
                <span className="block text-gray-700 dark:text-gray-300 text-2xl md:text-3xl font-medium">
                  I'm a product manager who solves business problems with technology.
                </span>
              </motion.h1>

              <motion.div
                variants={itemVariants}
                className="flex flex-col sm:flex-row flex-wrap gap-4 justify-center lg:justify-start mb-6"
              >
                <Link to="/projects">
                  <motion.button
                    whileHover={{ scale: 1.05, boxShadow: "0 10px 25px rgba(0,0,0,0.2)" }}
                    whileTap={{ scale: 0.95 }}
                    className="btn-modern flex items-center space-x-2"
                  >
                    <span>Check Out My Projects</span>
                    <FiArrowRight />
                  </motion.button>
                </Link>
                <Link to="/case-studies">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="btn-modern-outline flex items-center space-x-2"
                  >
                    <span>Read Case Studies</span>
                    <FiArrowRight />
                  </motion.button>
                </Link>
              </motion.div>

              <motion.div
                variants={itemVariants}
                className="flex flex-col sm:flex-row flex-wrap gap-4 justify-center lg:justify-start"
              >
                <Link to="/wait-list" className="text-gray-600 dark:text-gray-300 underline underline-offset-4 hover:text-gray-900 dark:hover:text-white transition-colors self-center">
                  Join my email list, the Wait List
                </Link>
                <span className="hidden sm:inline text-gray-300 dark:text-gray-700 self-center">&middot;</span>
                <Link to="/resume" className="text-gray-600 dark:text-gray-300 underline underline-offset-4 hover:text-gray-900 dark:hover:text-white transition-colors self-center">
                  See what I've done (resume)
                </Link>
              </motion.div>

              <motion.div
                variants={itemVariants}
                className="flex gap-4 justify-center lg:justify-start mt-8"
              >
                {[
                  { icon: FiLinkedin, href: 'https://www.linkedin.com/in/danielwait2/', label: 'LinkedIn' },
                  { icon: FiGithub, href: 'https://github.com/danielwait2', label: 'GitHub' },
                  { icon: FiYoutube, href: 'https://www.youtube.com/@danielwait', label: 'YouTube' },
                  { icon: FiMail, href: 'mailto:danielwait@waitworks.com', label: 'Email' },
                ].map(({ icon: Icon, href, label }) => (
                  <motion.a
                    key={label}
                    href={href}
                    target={href.startsWith('mailto:') ? undefined : '_blank'}
                    rel={href.startsWith('mailto:') ? undefined : 'noopener noreferrer'}
                    aria-label={label}
                    whileHover={{ scale: 1.15, y: -3 }}
                    whileTap={{ scale: 0.9 }}
                    className="w-11 h-11 flex items-center justify-center border border-gray-300 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-black hover:text-white hover:border-black dark:hover:bg-white dark:hover:text-black dark:hover:border-white transition-all"
                  >
                    <Icon className="w-5 h-5" />
                  </motion.a>
                ))}
              </motion.div>
            </motion.div>

            {/* Profile Image */}
            <motion.div
              variants={itemVariants}
              className="flex justify-center lg:justify-end"
            >
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="relative"
              >
                <div className="relative w-80 md:w-96 lg:w-[28rem] border border-gray-200 dark:border-gray-800">
                  <a
                    href="https://www.linkedin.com/in/danielwait2"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full group"
                  >
                    <img
                      src="/assets/profile.png"
                      alt="Daniel Wait"
                      className="w-full h-auto object-contain group-hover:opacity-90 transition-opacity duration-300"
                    />
                    <div className="absolute inset-0 bg-black dark:bg-white opacity-0 group-hover:opacity-20 dark:group-hover:opacity-10 transition-opacity duration-300 flex items-center justify-center backdrop-blur-sm">
                      <FiLinkedin className="text-white dark:text-black text-5xl drop-shadow-lg" />
                    </div>
                  </a>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* Currently Building + GitHub Activity */}
      <section className="py-16 bg-gray-50 dark:bg-gray-950 border-t border-gray-100 dark:border-gray-900">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
            className="rounded-xl border border-green-400 dark:border-green-600 bg-green-50 dark:bg-green-950/30 p-8"
          >
            {/* Header */}
            <div className="flex items-center gap-2 mb-8">
              <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              <span className="text-xs font-semibold uppercase tracking-widest text-green-600 dark:text-green-400">
                Actively Building
              </span>
            </div>

            {/* Two columns: Ember info | GitHub chart */}
            <div className="grid lg:grid-cols-2 gap-10 items-start">
              {/* Left: Ember */}
              <div className="text-center sm:text-left">
                <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-1">Ember</h2>
                <p className="text-green-700 dark:text-green-400 text-sm mb-4">Bedtime stories from the people who love you</p>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-6">
                  A family audio platform that lets grandparents and loved ones record bedtime stories kids can listen to anytime. AI helps when words run out. No feeds, no algorithms — just your family's voices, saved.
                </p>
                <div className="flex flex-wrap gap-3 justify-center sm:justify-start">
                  <a
                    href="https://www.emberstorytime.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-modern inline-flex items-center space-x-2 text-sm"
                  >
                    <span>Visit Ember</span>
                    <FiArrowRight className="w-4 h-4" />
                  </a>
                  <Link to="/projects/ember" className="btn-modern-outline inline-flex items-center space-x-2 text-sm">
                    <span>Project Details</span>
                    <FiArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>

              {/* Right: GitHub chart */}
              <div className="min-w-0 w-full overflow-hidden">
                <GitHubChart />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <LogoCloud />

      {/* About Section */}
      <section
        className="py-20 bg-white dark:bg-black border-t border-gray-100 dark:border-gray-900"
      >
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8"
        >
          <motion.div variants={itemVariants} className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 gradient-text">
              About Me
            </h2>
          </motion.div>
          
          <motion.div
            variants={itemVariants}
            className="prose prose-lg dark:prose-invert max-w-none"
          >
            <p className="text-gray-700 dark:text-gray-300 mb-6 text-lg leading-relaxed">
              Hi, I'm <strong className="text-gray-900 dark:text-white">Daniel Wait</strong>. I'm a product manager and MISM graduate student at BYU, focused on shipping products that create measurable outcomes.
            </p>

            <p className="text-gray-700 dark:text-gray-300 mb-6 text-lg leading-relaxed">
              At <strong className="text-gray-900 dark:text-white">Red Ventures</strong>, I worked as a PM intern on Sage Home Loans — the exclusive mortgage lender powering Robinhood Gold — where my work contributed to $500K+ in monthly recurring revenue and a 150% improvement in funnel conversion. At the <strong className="text-gray-900 dark:text-white">Missionary Training Center</strong>, I owned the roadmap for rebuilding a 150-schedule-per-day volunteer coordination system, eliminating 30+ hours of weekly manual work.
            </p>

            <p className="text-gray-700 dark:text-gray-300 mb-6 text-lg leading-relaxed">
              I also founded <strong className="text-gray-900 dark:text-white">Wait Works LLC</strong>, a technology consulting firm that has generated $80,000 in additional client revenue and cut administrative costs by 35%. Outside of consulting, I'm co-founder and PM at <strong className="text-gray-900 dark:text-white">Ember</strong>, a family audio platform for recording bedtime stories at{' '}
              <a href="https://www.emberstorytime.com" target="_blank" rel="noopener noreferrer" className="text-gray-900 dark:text-white underline">emberstorytime.com</a>.
            </p>

            <p className="text-gray-700 dark:text-gray-300 text-lg leading-relaxed">
              I care about defining the right problem, aligning the right team, and shipping something people actually use.
            </p>
          </motion.div>
        </motion.div>
      </section>

      {/* Work Section */}
      <section
        className="py-20 bg-gray-50 dark:bg-gray-950 border-t border-gray-100 dark:border-gray-900"
      >
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"
        >
          <motion.div variants={itemVariants} className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 gradient-text">
              My Work
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400">
              Discover <strong>Wait Works LLC</strong> and explore my <strong>Wait List</strong> collection
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            <motion.div
              variants={itemVariants}
              whileHover={{ y: -8 }}
              className="card-modern p-8 group"
            >
              <div className="w-16 h-16 rounded-2xl gradient-primary flex items-center justify-center mb-6 shadow-lg">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">Wait Works LLC</h3>
              <p className="text-gray-600 dark:text-gray-300 mb-6 leading-relaxed">
                Professional technology consulting and business solutions. Founded by <strong>Daniel Wait</strong>, 
                <strong>Wait Works LLC</strong> specializes in custom software development, web applications, 
                and system integration to streamline your business operations.
              </p>
              <Link to="/wait-works">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="btn-modern-outline"
                >
                  Learn More
                </motion.button>
              </Link>
            </motion.div>

            <motion.div
              variants={itemVariants}
              whileHover={{ y: -8 }}
              className="card-modern p-8 group"
            >
              <div className="w-16 h-16 rounded-2xl gradient-primary flex items-center justify-center mb-6 shadow-lg">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">Wait List</h3>
              <p className="text-gray-600 dark:text-gray-300 mb-6 leading-relaxed">
                Curated collection of interesting links, resources, and insights by <strong>Daniel Wait</strong>. 
                The <strong>Wait List</strong> features technology trends, innovation insights, and thought-provoking 
                content that I find valuable and worth sharing.
              </p>
              <Link to="/wait-list">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="btn-modern-outline"
                >
                  Explore Collection
                </motion.button>
              </Link>
            </motion.div>
          </div>
        </motion.div>
      </section>
    </>
  );
}

export default Home;
